import { useEffect, useState } from "react";
/* eslint-disable react/no-unescaped-entities, react/no-unstable-nested-components */
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Reveal from "@/components/Reveal";
import { api } from "@/lib/api";

export default function ArticleDetail() {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [articles, setArticles] = useState([]);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        api.get(`/articles/${slug}`)
            .then(({ data }) => setArticle(data))
            .catch(() => setNotFound(true));
        api.get(`/articles`)
            .then(({ data }) => setArticles(data))
            .catch(() => setArticles([]));
    }, [slug]);

    if (notFound) return <Navigate to="/articles" replace />;
    if (!article)
        return (
            <div className="bg-xv-ivory min-h-screen pt-44 px-6 text-center text-xv-text-secondary font-light">
                Chargement…
            </div>
        );

    const idx = articles.findIndex((a) => a.slug === slug);
    const next =
        articles.length > 0
            ? articles[(idx + 1) % articles.length]
            : null;

    return (
        <div data-testid="article-detail-page" className="bg-xv-ivory">
            <section className="pt-32 md:pt-40 pb-16">
                <div className="max-w-[1100px] mx-auto px-6 md:px-12">
                    <Link
                        to="/articles"
                        data-testid="back-to-articles"
                        className="inline-flex items-center gap-3 text-xv-midnight text-[11px] tracking-[0.28em] uppercase link-underline mb-12"
                    >
                        <ArrowLeft strokeWidth={1.2} size={16} />
                        Tous les articles
                    </Link>
                    <Reveal>
                        <div className="flex items-center gap-4 mb-8 text-xv-gold">
                            <span className="divider-gold" />
                            <span className="overline">
                                {article.kicker} · {article.readTime}
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1
                            className="font-serif text-xv-black text-[44px] sm:text-6xl md:text-7xl lg:text-[80px] leading-[1.0] tracking-tight"
                            style={{ fontWeight: 300 }}
                        >
                            {article.title}
                        </h1>
                    </Reveal>
                    <Reveal delay={0.18}>
                        <div className="mt-10 flex items-center gap-4 text-[10px] tracking-[0.32em] uppercase text-xv-gold">
                            <img
                                src={article.author_photo || "/brand/joel-kotho-avatar.jpg"}
                                alt={article.author || "Joel Kotho"}
                                className="w-12 h-12 rounded-full object-cover border border-xv-gold/40"
                            />
                            <div className="flex flex-col gap-1">
                                <span data-testid="article-byline" className="text-xv-gold">
                                    Par {article.author || "Joel Kotho"}
                                </span>
                                <span className="text-xv-text-secondary/70 normal-case tracking-normal text-[13px] font-serif italic">
                                    {article.author_role || "Fondateur & Managing Partner"}
                                </span>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {article.image && (
                <section className="pb-16">
                    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                        <Reveal>
                            <div className="overflow-hidden">
                                <img
                                    src={article.image}
                                    alt={article.title}
                                    className="w-full h-[360px] md:h-[560px] lg:h-[640px] object-cover"
                                />
                            </div>
                        </Reveal>
                    </div>
                </section>
            )}

            <section className="pb-28 md:pb-40">
                <div className="max-w-[820px] mx-auto px-6 md:px-12">
                    <div className="text-xv-text-secondary text-[17px] md:text-[19px] leading-[1.85] font-light">
                        {article.body_markdown ? (
                            <MarkdownArticle markdown={article.body_markdown} />
                        ) : (
                            <div className="space-y-8">
                                {(article.body || []).map((p, i) => (
                                    <Reveal key={i} delay={i * 0.05}>
                                        <p
                                            className={
                                                i === 0
                                                    ? "first-letter:font-serif first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.9] first-letter:text-xv-midnight"
                                                    : ""
                                            }
                                        >
                                            {p}
                                        </p>
                                    </Reveal>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="mt-20 pt-10 border-t border-xv-line">
                        <Reveal>
                            <div className="overline text-xv-gold mb-4">
                                Signature
                            </div>
                            <div className="flex items-center gap-5">
                                <img
                                    src={article.author_photo || "/brand/joel-kotho-avatar.jpg"}
                                    alt={article.author || "Joel Kotho"}
                                    className="w-16 h-16 rounded-full object-cover border border-xv-gold/40"
                                />
                                <div>
                                    <div className="font-serif text-xv-black text-[28px] md:text-[32px] leading-tight" style={{ fontWeight: 400 }}>
                                        {article.author || "Joel Kotho"}
                                    </div>
                                    <div className="mt-1 text-[12px] tracking-[0.28em] uppercase text-xv-gold/85">
                                        {article.author_role || "Fondateur & Managing Partner"} · XV Capital Partners
                                    </div>
                                </div>
                            </div>
                            <p className="mt-8 text-xv-text-secondary text-[15px] leading-[1.85] font-light italic max-w-xl">
                                Cet article reflète l'approche du cabinet.
                                Pour toute discussion confidentielle, prenez
                                contact directement avec nous.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {next && (
                <section className="bg-xv-cream py-24 md:py-32">
                    <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center">
                        <Reveal>
                            <Link
                                to={`/articles/${next.slug}`}
                                data-testid="next-article"
                                className="group block"
                            >
                                <div className="overline text-xv-gold mb-4">
                                    Article suivant
                                </div>
                                <h3
                                    className="font-serif text-3xl md:text-5xl text-xv-black leading-[1.1] transition-colors duration-500 group-hover:text-xv-midnight"
                                    style={{ fontWeight: 400 }}
                                >
                                    {next.title}
                                </h3>
                                <div className="mt-6 inline-flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-xv-midnight link-underline">
                                    Lire l'article
                                    <ArrowUpRight strokeWidth={1.2} size={16} />
                                </div>
                            </Link>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="lg:text-right">
                                <div className="overline text-xv-gold mb-4">
                                    Échangeons
                                </div>
                                <h3
                                    className="font-serif text-3xl md:text-4xl text-xv-black leading-[1.15] max-w-md lg:ml-auto"
                                    style={{ fontWeight: 400 }}
                                >
                                    Une question, un projet, un besoin
                                    d'expertise ?
                                </h3>
                                <Link
                                    to="/contact"
                                    data-testid="article-cta"
                                    className="mt-8 inline-flex items-center gap-4 px-8 py-4 bg-xv-midnight text-xv-ivory text-[11px] tracking-[0.3em] uppercase hover:bg-xv-black transition-all duration-700"
                                >
                                    Prendre contact
                                    <ArrowUpRight strokeWidth={1.2} size={16} />
                                </Link>
                            </div>
                        </Reveal>
                    </div>
                </section>
            )}
        </div>
    );
}

// Markdown renderer with premium editorial styles
function MarkdownArticle({ markdown }) {
    return (
        <div className="prose-premium">
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    h2: ({ node, ...props }) => (
                        <h2
                            className="font-serif text-xv-black text-[28px] md:text-[34px] leading-tight mt-16 mb-6"
                            style={{ fontWeight: 400 }}
                            {...props}
                        />
                    ),
                    h3: ({ node, ...props }) => (
                        <h3
                            className="font-serif text-xv-black text-[20px] md:text-[24px] leading-snug mt-10 mb-4"
                            style={{ fontWeight: 400 }}
                            {...props}
                        />
                    ),
                    p: ({ node, ...props }) => (
                        <p className="my-6" {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                        <ul className="my-6 space-y-2 list-none pl-0" {...props} />
                    ),
                    ol: ({ node, ...props }) => (
                        <ol className="my-6 space-y-2 list-decimal pl-6" {...props} />
                    ),
                    li: ({ node, ordered, ...props }) => (
                        <li
                            className={
                                ordered
                                    ? "pl-2"
                                    : "pl-6 relative before:content-['—'] before:absolute before:left-0 before:text-xv-gold"
                            }
                            {...props}
                        />
                    ),
                    strong: ({ node, ...props }) => (
                        <strong className="text-xv-black font-semibold" {...props} />
                    ),
                    em: ({ node, ...props }) => (
                        <em className="italic text-xv-black/85" {...props} />
                    ),
                    blockquote: ({ node, ...props }) => (
                        <blockquote
                            className="my-8 border-l-2 border-xv-gold pl-6 italic text-xv-black/80 font-serif text-[19px] leading-[1.85]"
                            {...props}
                        />
                    ),
                    table: ({ node, ...props }) => (
                        <div className="my-8 overflow-x-auto">
                            <table
                                className="w-full text-[14px] font-light border-collapse"
                                {...props}
                            />
                        </div>
                    ),
                    th: ({ node, ...props }) => (
                        <th
                            className="text-left font-serif text-[13px] tracking-[0.18em] uppercase text-xv-gold border-b border-xv-line pb-3 pr-4"
                            {...props}
                        />
                    ),
                    td: ({ node, ...props }) => (
                        <td
                            className="py-3 pr-4 border-b border-xv-line/50 align-top"
                            {...props}
                        />
                    ),
                    a: ({ node, ...props }) => (
                        <a
                            className="text-xv-midnight underline decoration-xv-gold/50 underline-offset-4 hover:decoration-xv-gold"
                            {...props}
                        />
                    ),
                    hr: () => (
                        <hr className="my-14 border-none h-px bg-xv-line" />
                    ),
                }}
            >
                {markdown}
            </ReactMarkdown>
        </div>
    );
}
